/**
 * Utility functions for calculating map scale distances based on zoom level
 */

export class MapScaleHelper {
  /**
   * Calculate the approximate distance in meters represented by a given pixel width at a specific zoom level
   * This uses the exact same formula that Mapbox's scale control uses
   * Formula: meters = (pixels * 78271.51696 * Math.cos(lat * Math.PI/180)) / (2^zoom)
   * Where 78271.51696 is half of 156543.03392 (adjusted for 512px Mapbox tiles)
   *
   * @param zoom - Map zoom level (0-22)
   * @param pixels - Width in pixels
   * @param latitude - Latitude for more accurate calculation (optional, defaults to 0)
   * @returns Distance in meters
   */
  static calculateDistanceAtZoom(zoom: number, pixels: number, latitude: number = 0): number {
    // Meters per pixel at zoom level 0 at equator for Mapbox (512px tiles)
    const metersPerPixelAtZoom0 = 78271.51696

    // Calculate meters per pixel at given zoom level and latitude
    const metersPerPixel = metersPerPixelAtZoom0 * Math.cos(latitude * Math.PI / 180) / Math.pow(2, zoom)

    return metersPerPixel * pixels
  }

  /**
   * Get a human-readable scale distance for a given zoom level
   * Returns appropriate distance values (in km or m) that make sense for the current zoom
   * This algorithm mimics Mapbox's scale control behavior
   *
   * @param zoom - Map zoom level
   * @param containerWidth - Width of the scale bar container in pixels
   * @param latitude - Latitude for more accurate calculation
   * @returns Object containing distance value and unit
   */
  static getScaleDistance(zoom: number, containerWidth: number = 150, latitude: number = 0): { value: number, unit: string, pixels: number } {
    // Calculate the maximum distance that fits in the container
    const maxDistance = this.calculateDistanceAtZoom(zoom, containerWidth, latitude)

    // Standard scale values that Mapbox uses (in meters)
    const standardValues = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000]

    // Find the largest standard value that is <= maxDistance
    let bestValue = 1
    for (const value of standardValues) {
      if (value <= maxDistance) {
        bestValue = value
      } else {
        break
      }
    }

    // Calculate the pixel width needed for this value
    const metersPerPixel = this.calculateDistanceAtZoom(zoom, 1, latitude)
    const bestWidth = bestValue / metersPerPixel

    // Ensure the width doesn't exceed the container
    const actualWidth = Math.min(bestWidth, containerWidth)
    const actualValue = bestValue * (actualWidth / bestWidth)

    // Convert to appropriate unit
    if (actualValue >= 1000) {
      return {
        value: Math.round(actualValue / 100) / 10, // Convert to km with 1 decimal
        unit: 'km',
        pixels: actualWidth
      }
    } else {
      return {
        value: Math.round(actualValue),
        unit: 'm',
        pixels: actualWidth
      }
    }
  }

}